import { setIdHeader, setToken } from "@/clients/open_api_injection";
import { UsersService } from "@/clients/services/UsersService";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import type { CurrentUser } from "@/hooks/requests/user/use-get-user";
import { UserIdSchema } from "@/schemas/user-schema";
import { markSignedInBefore } from "@/utils/has-signed-in-before";
import { decodeJWT } from "@/utils/jwt";
import { setLocalStorageJson } from "@/utils/local-storage";
import { setUserInfo } from "@/utils/sentry/tools";
import { setMixpanelUser } from "@/utils/mixpanel";
import { setSmartlookUser } from "@/utils/smartlook";
import { useAuth, useSignIn, useUser } from "@clerk/clerk-react";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useToast } from "../ui/use-toast";

export const useResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, setActive } = useSignIn();
  const { showError, showSuccess } = useToast();
  const navigation = useIonRouter();
  const { user } = useUser();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const processToken = async (email: string) => {
    const token = await getToken();

    if (!token) {
      throw new Error("No token found");
    }

    // Decode the token to get user info
    let userId = user?.id;

    const decodedToken = decodeJWT(token);
    setToken(token);
    userId = userId || decodedToken?.sub;
    setIdHeader(userId!);

    const { data } = await UsersService.loginApiV1UsersLoginPost(userId!);
    const isOnboardingCompleted = data.isOnboardingCompleted;

    const dataStorage = UserIdSchema.parse({
      email,
      isOnboardingCompleted,
      id: userId!,
    });
    setLocalStorageJson(LocalStorageKeys.USER_KEY, dataStorage);
    markSignedInBefore();
    queryClient.setQueryData<CurrentUser>([QueryKeys.USER], (prev) => ({
      ...(prev ?? {}),
      id: userId!,
      isOnboardingCompleted,
    }));
    // Clerk's `setActive` flips `isSignedIn` to true *before* this function
    // writes USER_KEY, which can cause `useGetSubscriptionStatus` to auto-enable
    // and run its queryFn against an empty USER_KEY — the queryFn throws,
    // the query enters error state, and with `retry: false` it never recovers.
    // Force a refetch now that USER_KEY is in place.
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.SUBSCRIPTION_STATUS],
    });
    // The setQueryData above only seeds a partial user ({ id, isOnboardingCompleted });
    // setQueryData marks it fresh, so on a cold (cache-cleared) login useGetUser would not
    // refetch within staleTime and personalInfo.firstName stays undefined — breaking the
    // name on Profile and AI chat. Invalidate to pull the full user from the server.
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.USER],
    });
    setUserInfo(dataStorage);
    await setSmartlookUser(userId!, email);
    setMixpanelUser(userId!, email);
  };

  const handleSendCode = async (email: string) => {
    if (!signIn) {
      throw new Error("SignIn not initialized");
    }

    try {
      setIsLoading(true);
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
      const searchParams = new URLSearchParams({
        email,
      });
      navigation.push(`/reset-password-verification?${searchParams.toString()}`, "forward");
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message || "Failed to send reset code.");
      } else {
        showError("Failed to send reset code.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (code: string, password: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get("email");
    if (!email) {
      throw new Error("Email is required");
    }

    if (!signIn) {
      throw new Error("SignIn not initialized");
    }

    try {
      setIsLoading(true);
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password,
      });

      if (result.status === "complete") {
        showSuccess("Password reset successfully!");
        await setActive({ session: result.createdSessionId });
        await processToken(email);
        navigation.push("/plans/my-plan", "forward", "replace");
      } else {
        showError("Something went wrong.");
      }
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message || "Failed to reset password.");
      } else {
        showError("Failed to reset password.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleSendCode,
    handleResetPassword,
    isLoading,
  };
};
